// 화면 17 — Leading Sector Timeline (Phase A) 메인 페이지.
// 라우트: /leading/sectors
// 권위: ARCH_DASHBOARD §4.17 / §6.2 (v1.10), PRD_Dashboard §5.7 (v1.1.1).
// 폴링 60s, WebSocket 미사용 (ADR-DASH-06).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import type { ApiError } from '../../api/client';
import {
  defaultLeadingSectorsFilters,
  type LeadingSectorsFilters,
  toYmd,
} from './leadingSectors.types';
import {
  MAX_LOOKBACK_DAYS,
  useDailyTop3Sparkline,
  useDivergencePanel,
  useSmoothedHistory,
} from './useLeadingSectors';
import { SmoothedTop3LineChart } from './SmoothedTop3LineChart';
import { DivergencePanel } from './DivergencePanel';
import { SectorSparklineGrid } from './SectorSparklineGrid';
import { SectorRecomputeModal } from './SectorRecomputeModal';
import { useSectorRecompute } from './useSectorRecompute';
import './leadingSectors.css';
import '../PendingCAEvents/pendingCa.css';

interface ToastItem {
  id: string;
  msg: string;
  tone: 'info' | 'success' | 'error' | 'warn';
}

// 필터 ↔ 쿼리스트링 동기화.
// `dateExplicit`: URL 에 `date` 가 명시되어 있는지. false 면 smoothed max date 로 자동 대체 후보 (이슈 3).
function filtersFromSearchParams(
  sp: URLSearchParams
): LeadingSectorsFilters & { dateExplicit: boolean } {
  const def = defaultLeadingSectorsFilters();
  const from = sp.get('from') ?? def.from;
  const to = sp.get('to') ?? def.to;
  const dateParam = sp.get('date');
  const divergenceDate = dateParam ?? to;
  // AC-DB-PRD-16 — 90일 cap. 사용자 입력이 그를 넘으면 to 기준 -90일로 보정.
  const clamped = clampRange(from, to);
  return {
    from: clamped.from,
    to: clamped.to,
    divergenceDate,
    dateExplicit: dateParam !== null,
  };
}

function filtersToSearchParams(f: LeadingSectorsFilters): URLSearchParams {
  const def = defaultLeadingSectorsFilters();
  const sp = new URLSearchParams();
  if (f.from !== def.from) sp.set('from', f.from);
  if (f.to !== def.to) sp.set('to', f.to);
  if (f.divergenceDate !== f.to) sp.set('date', f.divergenceDate);
  return sp;
}

function clampRange(from: string, to: string): { from: string; to: string } {
  const fd = new Date(from);
  const td = new Date(to);
  if (Number.isNaN(fd.getTime()) || Number.isNaN(td.getTime())) {
    return defaultLeadingSectorsFilters();
  }
  if (fd > td) return { from: to, to };
  // 90일 cap (AC-DB-PRD-16, sparkline 한정. smoothed 라인은 백엔드에서 365일까지 허용하지만
  // UI 는 sparkline 와 같은 창을 쓰므로 동일 cap).
  const diff = Math.round((td.getTime() - fd.getTime()) / (1000 * 60 * 60 * 24));
  if (diff > MAX_LOOKBACK_DAYS) {
    const newFrom = new Date(td);
    newFrom.setDate(newFrom.getDate() - MAX_LOOKBACK_DAYS);
    return { from: toYmd(newFrom), to };
  }
  return { from, to };
}

export default function LeadingSectorsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  const parsed = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const filters: LeadingSectorsFilters = useMemo(
    () => ({ from: parsed.from, to: parsed.to, divergenceDate: parsed.divergenceDate }),
    [parsed]
  );

  const setFilters = useCallback(
    (next: LeadingSectorsFilters) => {
      const clamped = clampRange(next.from, next.to);
      setSearchParams(
        filtersToSearchParams({ ...next, from: clamped.from, to: clamped.to }),
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const smoothed = useSmoothedHistory(filters.from, filters.to);
  const daily = useDailyTop3Sparkline(filters.from, filters.to);

  const smoothedRows = smoothed.data ?? [];
  const dailyRows = daily.data ?? [];

  // smoothed history 응답의 max trading_date — divergence default 자동 선택 후보 (이슈 3).
  const smoothedMaxDate = useMemo(() => {
    if (smoothedRows.length === 0) return null;
    let mx = smoothedRows[0].trading_date;
    for (const r of smoothedRows) {
      if (r.trading_date > mx) mx = r.trading_date;
    }
    return mx;
  }, [smoothedRows]);

  // 이슈 3 — URL 에 date 가 명시되지 않았고 smoothed max < to 면 max 로 자동 보정.
  // 사용자가 명시적으로 변경한 경우 (dateExplicit=true) 는 건드리지 않는다.
  useEffect(() => {
    if (parsed.dateExplicit) return;
    if (!smoothed.isSuccess) return;
    if (!smoothedMaxDate) return;
    if (smoothedMaxDate === filters.divergenceDate) return;
    // smoothed 최신 일자가 to 보다 과거이면 (오늘 미적재 등) 자동 보정.
    if (smoothedMaxDate < filters.divergenceDate) {
      const sp = new URLSearchParams(searchParams);
      sp.set('date', smoothedMaxDate);
      setSearchParams(sp, { replace: true });
    }
  }, [
    parsed.dateExplicit,
    smoothed.isSuccess,
    smoothedMaxDate,
    filters.divergenceDate,
    searchParams,
    setSearchParams,
  ]);

  const divergence = useDivergencePanel(filters.divergenceDate);

  const lastDate = useMemo(() => {
    const dates: string[] = [];
    for (const r of smoothedRows) dates.push(r.trading_date);
    for (const r of dailyRows) dates.push(r.trading_date);
    if (dates.length === 0) return null;
    dates.sort();
    return dates[dates.length - 1];
  }, [smoothedRows, dailyRows]);

  // ── 섹터 스코어 재산정 ──
  const recompute = useSectorRecompute();
  const [recomputeModalOpen, setRecomputeModalOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const pushToast = useCallback(
    (msg: string, tone: ToastItem['tone'] = 'info') => {
      const id = Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, msg, tone }]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4500);
    },
    []
  );

  // completed/failed 상태 감지 — toast 후 idle 복귀.
  useEffect(() => {
    if (recompute.phase === 'completed') {
      pushToast('섹터 스코어 재산정 완료. 화면 새로고침을 권장합니다.', 'success');
      recompute.acknowledge();
    } else if (recompute.phase === 'failed') {
      pushToast(`재산정 실패: ${recompute.errorMessage ?? '알 수 없는 오류'}`, 'error');
      recompute.acknowledge();
    }
  }, [recompute.phase, recompute.errorMessage, recompute, pushToast]);

  const handleRecomputeSubmit = useCallback(
    async (lookbackDays: number, fullReset: boolean, recomputeLeaderStocks: boolean) => {
      const result = await recompute.submit({ lookbackDays, fullReset, recomputeLeaderStocks });
      if (!result.ok && result.errorMessage) {
        pushToast(result.errorMessage, 'warn');
      }
    },
    [recompute, pushToast]
  );

  // 헤더 버튼 라벨 — running 시 진행률 표시.
  const recomputeBtnLabel = useMemo(() => {
    if (recompute.phase === 'running' && recompute.activeJob) {
      const j = recompute.activeJob;
      const pct = j.total > 0 ? Math.round((j.processed / j.total) * 100) : 0;
      return `재산정 중 ${j.processed}/${j.total} (${pct}%)`;
    }
    if (recompute.phase === 'submitting') return '요청 중…';
    return '섹터 스코어 재산정';
  }, [recompute.phase, recompute.activeJob]);

  return (
    <div className="ls-page" data-testid="leading-sectors-page">
      <header className="ls-page__header" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
          <h1 className="ls-page__title">주도 섹터 시계열</h1>
          <span className="ls-muted">
            Phase A · 60일 smoothed Top3 / 당일 vs smoothed 격차 / 섹터 sparkline
            {(smoothed.isFetching || daily.isFetching || divergence.isFetching) && ' · 갱신 중…'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {lastDate && <span className="ls-muted">최근 일자: {lastDate}</span>}
          <button
            type="button"
            className="pca-btn"
            onClick={() => setRecomputeModalOpen(true)}
            disabled={recompute.phase === 'submitting'}
            data-testid="sector-recompute-trigger"
            title="섹터 스코어를 lookback 일수 기준으로 재산정"
          >
            <RefreshCw size={14} className={recompute.phase === 'running' ? 'pca-spin' : undefined} />
            {recomputeBtnLabel}
          </button>
        </div>
      </header>

      <FilterBar filters={filters} onChange={setFilters} />

      {smoothed.isError && (
        <div className="ls-banner ls-banner--error">
          smoothed 이력 로드 실패: {(smoothed.error as Error).message}
        </div>
      )}
      {daily.isError && (
        <div className="ls-banner ls-banner--error">
          daily Top3 sparkline 로드 실패: {(daily.error as Error).message}
        </div>
      )}

      {/* 상단 — 60일 smoothed Top3 라인 차트 */}
      <section className="ls-panel" aria-labelledby="ls-line-h">
        <div className="ls-panel__header">
          <h2 className="ls-panel__title" id="ls-line-h">
            60일 Smoothed Top3 라인
          </h2>
          <span className="ls-panel__hint">
            is_smoothed_lead=TRUE 행 · {smoothedRows.length} row
          </span>
        </div>
        <SmoothedTop3LineChart rows={smoothedRows} isLoading={smoothed.isLoading} />
      </section>

      {/* 중단 — Divergence 패널 */}
      <section className="ls-panel" aria-labelledby="ls-div-h">
        <div className="ls-panel__header">
          <h2 className="ls-panel__title" id="ls-div-h">
            당일 vs Smoothed Top3 격차 패널
          </h2>
          <div className="ls-panel__controls">
            <label className="ls-filter__label" htmlFor="ls-div-date-inline">
              기준일
            </label>
            <input
              id="ls-div-date-inline"
              type="date"
              className="ls-filter__input ls-filter__input--sm"
              value={filters.divergenceDate}
              min={filters.from}
              max={filters.to}
              onChange={(e) => setFilters({ ...filters, divergenceDate: e.target.value })}
              data-testid="ls-divergence-date-input"
            />
            {smoothedMaxDate && smoothedMaxDate !== filters.divergenceDate && (
              <button
                type="button"
                className="ls-panel__hint-btn"
                onClick={() => setFilters({ ...filters, divergenceDate: smoothedMaxDate })}
                title="가장 최근 적재일로 이동"
              >
                최근 적재일 ({smoothedMaxDate})
              </button>
            )}
          </div>
        </div>
        <DivergencePanel
          data={divergence.data}
          isLoading={divergence.isLoading}
          error={divergence.error ? toApiErrorShape(divergence.error) : null}
          date={filters.divergenceDate}
        />
      </section>

      {/* 하단 — Sparkline 그리드 */}
      <section className="ls-panel" aria-labelledby="ls-spark-h">
        <div className="ls-panel__header">
          <h2 className="ls-panel__title" id="ls-spark-h">
            섹터 Sparkline 그리드
          </h2>
          <span className="ls-panel__hint">
            breadth / peak / avg_score / stock_count · {dailyRows.length} row
          </span>
        </div>
        <SectorSparklineGrid rows={dailyRows} isLoading={daily.isLoading} />
      </section>

      {recomputeModalOpen && (
        <SectorRecomputeModal
          phase={recompute.phase}
          activeJob={recompute.activeJob}
          onSubmit={handleRecomputeSubmit}
          onClose={() => setRecomputeModalOpen(false)}
        />
      )}

      <div className="pca-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`pca-toast pca-toast--${t.tone}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}

interface FilterBarProps {
  filters: LeadingSectorsFilters;
  onChange: (next: LeadingSectorsFilters) => void;
}

function FilterBar({ filters, onChange }: FilterBarProps): JSX.Element {
  return (
    <div className="ls-filterbar">
      <label className="ls-filter__label" htmlFor="ls-from">From</label>
      <input
        id="ls-from"
        type="date"
        className="ls-filter__input"
        value={filters.from}
        max={filters.to}
        onChange={(e) => onChange({ ...filters, from: e.target.value })}
      />
      <label className="ls-filter__label" htmlFor="ls-to">To</label>
      <input
        id="ls-to"
        type="date"
        className="ls-filter__input"
        value={filters.to}
        onChange={(e) =>
          onChange({
            ...filters,
            to: e.target.value,
            // to 가 바뀌면 divergenceDate 도 따라간다 (기본 동작).
            divergenceDate: filters.divergenceDate === filters.to ? e.target.value : filters.divergenceDate,
          })
        }
      />
      <label className="ls-filter__label" htmlFor="ls-divdate">Divergence 기준일</label>
      <input
        id="ls-divdate"
        type="date"
        className="ls-filter__input"
        value={filters.divergenceDate}
        max={filters.to}
        min={filters.from}
        onChange={(e) => onChange({ ...filters, divergenceDate: e.target.value })}
      />
      <span className="ls-muted">· cap {MAX_LOOKBACK_DAYS}일</span>
    </div>
  );
}

// react-query error 타입 — ApiError 확정 불가, 안전 추출.
function toApiErrorShape(
  err: unknown
): { code: string; message: string; httpStatus: number } | null {
  if (!err) return null;
  const e = err as Partial<ApiError>;
  return {
    code: e.code ?? 'unknown_error',
    message: e.message ?? 'unknown error',
    httpStatus: e.httpStatus ?? 0,
  };
}
